// Catálogo con desplazamiento infinito (cursor de la API) y búsqueda. Lo cargado queda en memoria unos minutos: volver
// del carrito al inicio muestra la misma lista, en el mismo lugar.
import { useCallback, useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { mensajeDeError, SinRed } from './api';
import { claveFuente, type Fuente, traerCatalogo } from './fuente';
import type { Articulo } from './tipos';

const VIGENCIA_MS = 5 * 60_000;
/** Con menos letras no se busca (una sola letra trae medio catálogo). */
export const MINIMO_BUSQUEDA = 2;

interface Lista {
  articulos: Articulo[];
  siguiente: string | null;
  hasta: number;
}

const cache = new Map<string, Lista>();
export const olvidarCatalogo = () => cache.clear();

/** Lo que el cliente está mirando en el inicio: sobrevive a ir al carrito y volver. */
export const useFiltros = create<{ texto: string; categoria: string | null }>(() => ({ texto: '', categoria: null }));

export const busquedaEfectiva = (texto: string): string => {
  const t = texto.trim();
  return t.length >= MINIMO_BUSQUEDA ? t : '';
};

/** El catálogo de la fuente (el negocio o la vista pública, decisión D49), por búsqueda o categoría. */
export function useCatalogo(fuente: Fuente | null, q: string, categoria: string | null) {
  const clave = fuente ? `${claveFuente(fuente)}|${q}|${categoria ?? ''}` : null;
  const fuenteRef = useRef(fuente);
  fuenteRef.current = fuente;
  const vigente = clave ? cache.get(clave) : undefined;
  const util = vigente && vigente.hasta > Date.now() ? vigente : undefined;
  const [lista, setLista] = useState<Lista | undefined>(util);
  const [cargando, setCargando] = useState(!util);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sinRed, setSinRed] = useState(false);
  const claveRef = useRef(clave);
  claveRef.current = clave;
  const ctrl = useRef<AbortController | null>(null);

  const cargar = useCallback(
    async (cursor: string | null) => {
      const k = claveRef.current;
      const f = fuenteRef.current;
      if (!k || !f) return;
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      if (cursor) setCargandoMas(true);
      else setCargando(true);
      setError(null);
      try {
        const r = await traerCatalogo(f, { q, categoria, cursor }, c.signal);
        if (claveRef.current !== k) return;
        const previa = cursor ? (cache.get(k)?.articulos ?? []) : [];
        // Sin repetidos por si una página se pidió dos veces.
        const vistos = new Set(previa.map((a) => a.id));
        const nueva: Lista = {
          articulos: [...previa, ...r.datos.filter((a) => !vistos.has(a.id))],
          siguiente: r.siguiente,
          hasta: Date.now() + VIGENCIA_MS,
        };
        cache.set(k, nueva);
        setLista(nueva);
        setSinRed(false);
      } catch (e) {
        if (c.signal.aborted || claveRef.current !== k) return;
        setError(mensajeDeError(e));
        setSinRed(e instanceof SinRed);
      } finally {
        if (claveRef.current === k && ctrl.current === c) {
          setCargando(false);
          setCargandoMas(false);
        }
      }
    },
    [q, categoria],
  );

  useEffect(() => {
    if (!clave) return;
    const v = cache.get(clave);
    if (v && v.hasta > Date.now()) {
      setLista(v);
      setCargando(false);
      setError(null);
      return;
    }
    setLista(v);
    void cargar(null);
    return () => ctrl.current?.abort();
  }, [clave, cargar]);

  const cargarMas = useCallback(() => {
    const actual = claveRef.current ? cache.get(claveRef.current) : undefined;
    if (!actual?.siguiente || cargandoMas) return;
    void cargar(actual.siguiente);
  }, [cargar, cargandoMas]);

  return {
    articulos: lista?.articulos ?? [],
    hayMas: !!lista?.siguiente,
    cargando: cargando && !lista,
    cargandoMas,
    error,
    sinRed,
    recargar: () => void cargar(null),
    cargarMas,
    reintentarMas: () => lista?.siguiente && void cargar(lista.siguiente),
  };
}

/** Llama a `f` cuando el elemento queda cerca de la vista (para traer la página siguiente). */
export function useAlLlegar(f: () => void, activo: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const fRef = useRef(f);
  fRef.current = f;
  useEffect(() => {
    const el = ref.current;
    if (!el || !activo || typeof IntersectionObserver === 'undefined') return;
    const o = new IntersectionObserver((e) => e[0]?.isIntersecting && fRef.current(), { rootMargin: '600px 0px' });
    o.observe(el);
    return () => o.disconnect();
  }, [activo]);
  return ref;
}
